import { useI18n } from '@muse/i18n'
import { DownloadIcon, PauseIcon, PlayIcon, ShareIcon } from '@ds/components'
import { shortCount } from '@core/format'
import { useHydratedValue } from '@muse-web/router'
import type { TAudioSimple } from '@core/models'
import { sendPlayPauseClick, useAudioPlayback } from '@features/audio-player'
import { useDownloadDialog } from '@features/audio-download'
import { type TShareAudioEntity, useShareDialog } from '@features/share'
import { TryForFreeCardContainer } from '@features/try-for-free'

import { AudioStats, type TAudioStatItem } from '../components/audio-stats/AudioStats'

type TAudioStatsContainerProps = {
    audio: TAudioSimple
}

export const AudioStatsContainer = ({ audio }: TAudioStatsContainerProps) => {
    const { t } = useI18n()
    const { isActiveAudio, isPlaying, playPause } = useAudioPlayback(audio)
    const isPlayingShown = useHydratedValue(isPlaying, false)
    const download = useDownloadDialog()
    const share = useShareDialog()

    const handlePlayToggle = () => {
        sendPlayPauseClick(audio.id, 'player_audio_page_stats', !isActiveAudio || !isPlaying)
        playPause()
    }

    const handleDownload = () => {
        download.open(
            {
                id: audio.id,
                title: audio.title,
                authorUsername: audio.authorUsername,
                authorId: audio.authorId,
                avatarUrl: audio.avatarUrl,
            },
            'player_audio_page_stats',
        )
    }

    const handleShare = () => {
        const entity: TShareAudioEntity = {
            item: 'audio',
            id: audio.id,
            title: audio.title,
            placement: 'player_audio_page_stats',
            authorUsername: audio.authorUsername,
            audioSlug: audio.slug,
        }
        share.open(entity)
    }

    const items: TAudioStatItem[] = [
        {
            id: 'plays',
            label: t('i18n.audioStatsPlays'),
            value: shortCount(audio.playsCount),
            Icon: isPlayingShown ? PauseIcon : PlayIcon,
            onClick: handlePlayToggle,
        },
        ...(audio.isDownloadable
            ? [
                  {
                      id: 'downloads',
                      label: t('i18n.audioStatsDownloads'),
                      value: shortCount(audio.downloadsCount),
                      Icon: DownloadIcon,
                      onClick: handleDownload,
                  },
              ]
            : []),
        {
            id: 'shares',
            label: t('i18n.audioStatsShares'),
            value: shortCount(audio.sharesCount),
            Icon: ShareIcon,
            onClick: handleShare,
        },
    ]

    return (
        <AudioStats
            items={items}
            upsellSlot={
                <TryForFreeCardContainer
                    event={{ name: 'audio_try_click', payload: {} }}
                    variant="small"
                />
            }
        />
    )
}
